import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Collapse, Modal } from 'react-bootstrap';

import './workday-topics.css';

import EnumSelectDropdown, { EnumSelectDropdownHandle } from '../Filters/EnumSelectDropdown';
import SearchableStringListDropdown, { SearchableStringListDropdownHandle } from '../Filters/SearchableStringListDropdown';
import WordSearchInput, { WordSearch, WordSearchInputHandle } from '../Filters/WordSearchInput';

import ChevronContract from '../Icons/Chevron/ChevronContract';
import ChevronDown from '../Icons/Chevron/ChevronDown';
import ChevronExpand from '../Icons/Chevron/ChevronExpand';
import Eye from '../Icons/Eye';
import ResetFilter from '../Icons/ResetFilter';

import { ActiveInactiveStatus } from '../../enums/ActiveInactiveStatus';
import { CourseAvailability } from '../../enums/CourseAvailability';
import { Sort } from '../../enums/Sort';

import { normalizeText } from '../../functions/TextHelpers';

import { WorkdayTopic } from '../../types/Workday/WorkdayTopic';


const DEFAULT_NAME_FILTER: WordSearch = { value: '', regex: undefined, sort: Sort.Ascending };

type WorkdayTopicsTableProps = {
    topicList: WorkdayTopic[];
};

const WorkdayTopicsTable = ({ topicList }: WorkdayTopicsTableProps) => {
    const [filteredList, setFilteredList] = useState<WorkdayTopic[]>(topicList);
    const [expandedKeys, setExpandedKeys] = useState<Set<number>>(new Set());
    const [auditTopic, setAuditTopic] = useState<WorkdayTopic | null>(null);

    const [showFilters, setShowFilters] = useState(false);

    const [nameFilter, setNameFilter] = useState<WordSearch>(DEFAULT_NAME_FILTER);
    const [descriptionFilter, setDescriptionFilter] = useState<RegExp | undefined>();
    /**
     * Preset to Active per #120943 UAT feedback: the page opens showing active topics only, and the
     * user widens it to Inactive or both from the control. Clearing the filter shows both.
     */
    const [statusFilter, setStatusFilter] = useState<ActiveInactiveStatus | undefined>(ActiveInactiveStatus.Active);
    /**
     * Preset to HasCourses per #120943 UAT feedback: the page opens with course-less topics hidden,
     * and the user widens it to NoCourses or ShowAll from the control. Never undefined — the dropdown
     * is not clearable, so ShowAll carries the "everything" state explicitly.
     */
    const [courseAvailabilityFilter, setCourseAvailabilityFilter] = useState<CourseAvailability>(CourseAvailability.HasCourses);
    const [courseIdFilter, setCourseIdFilter] = useState<string | undefined>();
    const [courseNameFilter, setCourseNameFilter] = useState<RegExp | undefined>();
    const [courseDescFilter, setCourseDescFilter] = useState<RegExp | undefined>();

    const nameRef = useRef<WordSearchInputHandle>(null);
    const descriptionRef = useRef<WordSearchInputHandle>(null);
    const statusRef = useRef<EnumSelectDropdownHandle>(null);
    const courseAvailabilityRef = useRef<EnumSelectDropdownHandle>(null);
    const courseIdRef = useRef<SearchableStringListDropdownHandle>(null);
    const courseNameRef = useRef<WordSearchInputHandle>(null);
    const courseDescRef = useRef<WordSearchInputHandle>(null);

    useEffect(() => {
        setExpandedKeys(new Set());
    }, [topicList]);

    const allCourseIds = useMemo(() => {
        const ids = new Set<string>();
        topicList.forEach(t => t.courses.forEach(c => ids.add(c.workdayLearningCourseId)));
        return Array.from(ids).sort();
    }, [topicList]);

    useEffect(() => {
        if (!topicList) return;

        const hasNameSort = nameFilter?.sort !== undefined;

        const filtered = topicList.filter(t => {
            const name = normalizeText(t.name);
            const description = normalizeText(t.description ?? '');

            const matchesName = nameFilter?.regex
                ? nameFilter.regex.test(name)
                : true;

            const matchesDescription = descriptionFilter
                ? descriptionFilter.test(description)
                : true;

            // !== undefined rather than truthiness: Active is 0 in the numeric enum.
            const matchesStatus = statusFilter !== undefined
                ? t.status === (statusFilter === ActiveInactiveStatus.Active)
                : true;

            const matchesCourseAvailability = courseAvailabilityFilter === CourseAvailability.ShowAll
                ? true
                : (courseAvailabilityFilter === CourseAvailability.HasCourses) === (t.courses.length > 0);

            const matchesCourseId = courseIdFilter
                ? t.courses.some(c => c.workdayLearningCourseId === courseIdFilter)
                : true;
            const matchesCourseName = courseNameFilter
                ? t.courses.some(c => courseNameFilter.test(normalizeText(c.name)))
                : true;
            const matchesCourseDesc = courseDescFilter
                ? t.courses.some(c => courseDescFilter.test(normalizeText(c.description ?? '')))
                : true;

            return matchesName && matchesDescription && matchesStatus && matchesCourseAvailability
                && matchesCourseId && matchesCourseName && matchesCourseDesc;
        });

        const sorted = [...filtered].sort((a, b) => {
            const nameA = normalizeText(a.name);
            const nameB = normalizeText(b.name);

            if (hasNameSort) {
                const cmp = nameA.localeCompare(nameB);
                return nameFilter.sort === Sort.Ascending ? cmp : -cmp;
            }

            return nameA.localeCompare(nameB);
        });

        setFilteredList(sorted);
    }, [topicList, nameFilter, descriptionFilter, statusFilter, courseAvailabilityFilter, courseIdFilter, courseNameFilter, courseDescFilter]);

    const toggleExpand = (id: number) => {
        setExpandedKeys(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const onRowKeyDown: React.KeyboardEventHandler<HTMLTableRowElement> = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleExpand(Number((e.currentTarget as HTMLElement).dataset.id));
        }
    };

    const resetFilters = () => {
        nameRef.current?.clear();
        descriptionRef.current?.clear();
        // reset, not clear: Reset Filters returns to the page-load state — Status Active, Courses Has Courses.
        statusRef.current?.reset();
        courseAvailabilityRef.current?.reset();
        courseIdRef.current?.clear();
        courseNameRef.current?.clear();
        courseDescRef.current?.clear();
    };

    const expandAll = () => setExpandedKeys(new Set(topicList.map(t => t.id)));
    const collapseAll = () => setExpandedKeys(new Set());

    const renderRows = () => (
        filteredList.map((t, i) => {
            const isExpanded = expandedKeys.has(t.id);
            const isOdd = i % 2 === 0;
            const rowClass = isOdd ? 'wt-row-odd' : 'wt-row-even';

            return (
                <React.Fragment key={t.id}>
                    {/* Parent row */}
                    <tr
                        data-id={t.id}
                        className={`align-middle cursor-pointer ${rowClass}`}
                        tabIndex={0}
                        onKeyDown={onRowKeyDown}
                        aria-expanded={isExpanded}
                        aria-controls={`wt-expand-${t.id}`}
                        style={{ userSelect: 'none' }}
                        onClick={() => toggleExpand(t.id)}
                    >
                        <td>
                            {t.name}
                            <span className={`wt-course-count${t.courses.length === 0 ? ' wt-course-count-empty' : ''}`}>
                                {t.courses.length} {t.courses.length === 1 ? 'course' : 'courses'}
                            </span>
                        </td>
                        <td>{t.description}</td>
                        <td>{t.status == null ? '—' : t.status ? 'Active' : 'Inactive'}</td>
                        <td className='text-center'>
                            <div className='row g-0 justify-content-center'>
                                <div className='col-auto'>
                                    <button
                                        type='button'
                                        title='View Workday IDs'
                                        className='btn p-0 border-0 bg-transparent me-2'
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setAuditTopic(t);
                                        }}
                                    >
                                        <Eye />
                                    </button>
                                </div>
                                <div className='col-auto'>
                                    <button
                                        type='button'
                                        title='Show courses'
                                        className='btn p-0 border-0 bg-transparent'
                                        aria-expanded={isExpanded}
                                        aria-controls={`wt-expand-${t.id}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleExpand(t.id);
                                        }}
                                    >
                                        <ChevronDown
                                            className='transition-chevron'
                                            style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                                        />
                                    </button>
                                </div>
                            </div>
                        </td>
                    </tr>

                    {/* Expanded courses row */}
                    <tr className={`wt-expanded-row ${rowClass}`}>
                        <td colSpan={4} className='p-0'>
                            <Collapse in={isExpanded}>
                                <div id={`wt-expand-${t.id}`} className={`border-top px-3 py-2 ${rowClass}`}>
                                    <div className='d-flex align-items-center mb-2'>
                                        <span className='text-muted small'>Associated Courses</span>
                                    </div>
                                    <table className='wt-course-table'>
                                        <colgroup>
                                            <col />
                                            <col />
                                            <col />
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th>Course Name</th>
                                                <th>Course ID</th>
                                                <th>Description</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {t.courses.length > 0
                                                ? t.courses.map((c, ci) => (
                                                    <tr key={c.id} className={ci % 2 === 0 ? 'wt-course-even' : 'wt-course-odd'}>
                                                        <td>{c.name}</td>
                                                        <td>{c.workdayLearningCourseId}</td>
                                                        <td>{c.description}</td>
                                                    </tr>
                                                ))
                                                : (
                                                    <tr>
                                                        <td colSpan={3} className='text-muted'>No courses linked.</td>
                                                    </tr>
                                                )
                                            }
                                        </tbody>
                                    </table>
                                </div>
                            </Collapse>
                        </td>
                    </tr>
                </React.Fragment>
            );
        })
    );

    const renderNoList = () => (
        <tr>
            <td className='px-2'><div className='text-muted'>None</div></td>
            <td className='px-2'><div className='text-muted'>None</div></td>
            <td className='px-2'><div className='text-muted'>None</div></td>
            <td className='px-2'></td>
        </tr>
    );

    const renderAuditModal = () => (
        <Modal
            show={auditTopic !== null}
            onHide={() => setAuditTopic(null)}
            size='lg'
            dialogClassName='wt-audit-dialog'
        >
            <Modal.Header closeButton>
                <Modal.Title className='w-100 text-center'>{auditTopic?.name} — Workday IDs</Modal.Title>
            </Modal.Header>
            <Modal.Body className='wt-audit'>
                {auditTopic && (
                    <>
                        <div className='wt-audit-heading'>Topic</div>
                        <table className='wt-audit-table mb-4'>
                            <colgroup>
                                <col />
                                <col />
                            </colgroup>
                            <tbody>
                                <tr className='wt-audit-odd'>
                                    <td>Workday ID</td>
                                    <td className='wt-audit-id' title={auditTopic.workdayId ?? undefined}>{auditTopic.workdayId ?? '—'}</td>
                                </tr>
                                <tr className='wt-audit-even'>
                                    <td>Workday Learning Topic ID</td>
                                    <td className='wt-audit-id' title={auditTopic.workdayLearningTopicId}>{auditTopic.workdayLearningTopicId}</td>
                                </tr>
                            </tbody>
                        </table>

                        <div className='wt-audit-heading'>Courses ({auditTopic.courses.length})</div>
                        <div className='wt-audit-course-scroll'>
                            <table className='wt-audit-course-table'>
                                <colgroup>
                                    <col />
                                    <col />
                                    <col />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>Course Name</th>
                                        <th>Workday ID</th>
                                        <th>Workday Learning Course ID</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {auditTopic.courses.length > 0
                                        ? auditTopic.courses.map((c, ci) => (
                                            <tr key={c.id} className={ci % 2 === 0 ? 'wt-audit-odd' : 'wt-audit-even'}>
                                                <td title={c.name}>{c.name}</td>
                                                <td className='wt-audit-id' title={c.workdayId ?? undefined}>{c.workdayId ?? '—'}</td>
                                                <td className='wt-audit-id' title={c.workdayLearningCourseId}>{c.workdayLearningCourseId}</td>
                                            </tr>
                                        ))
                                        : (
                                            <tr>
                                                <td colSpan={3} className='text-muted'>No courses linked.</td>
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button className='btn btn-primary' onClick={() => setAuditTopic(null)}>
                    Close
                </button>
            </Modal.Footer>
        </Modal>
    );

    return (
        <div className='border rounded-2 shadow-sm workday-topics'>
            {renderAuditModal()}
            <div className='wt-controls-bar border-bottom'>
                <div className='wt-controls-header'>
                    <div className='d-flex gap-1 flex-shrink-0'>
                        <button
                            className='btn btn-sm btn-outline-secondary'
                            title={showFilters ? 'Fewer filters' : 'More filters'}
                            onClick={() => setShowFilters(f => !f)}
                        >
                            <ChevronDown
                                className='transition-chevron'
                                style={{ transform: showFilters ? 'rotate(0deg)' : 'rotate(-90deg)' }}
                            />
                            {' '}Filters
                        </button>
                        <button
                            title='Reset filters'
                            className='btn btn-sm btn-primary'
                            onClick={resetFilters}
                        >
                            <ResetFilter />
                        </button>
                    </div>
                    <div className='wt-filter-primary'>
                        <div>
                            <WordSearchInput
                                label='Name'
                                sortable
                                sort={nameFilter.sort}
                                filterFillerWords={false}
                                ref={nameRef}
                                onChange={(filter: WordSearch) => {
                                    setNameFilter({
                                        ...filter,
                                        sort: filter.sort === undefined ? Sort.Ascending : filter.sort,
                                    });
                                }}
                                placeholder='Filter names...'
                            />
                        </div>
                        <div>
                            <WordSearchInput
                                label='Description'
                                ref={descriptionRef}
                                filterFillerWords={false}
                                onChange={({ regex }) => setDescriptionFilter(regex)}
                                placeholder='Filter description...'
                            />
                        </div>
                        <div>
                            <EnumSelectDropdown
                                label='Status'
                                ref={statusRef}
                                enumObj={ActiveInactiveStatus}
                                defaultValue={ActiveInactiveStatus.Active}
                                placeholder='Filter...'
                                onChange={(s) => setStatusFilter(s)}
                                clearable
                            />
                        </div>
                        <div>
                            <EnumSelectDropdown
                                label='Courses'
                                ref={courseAvailabilityRef}
                                enumObj={CourseAvailability}
                                defaultValue={CourseAvailability.HasCourses}
                                // not clearable, so onChange never yields undefined; the fallback satisfies the prop signature
                                onChange={(c) => setCourseAvailabilityFilter(c ?? CourseAvailability.HasCourses)}
                                clearable={false}
                            />
                        </div>
                    </div>
                    <div className='d-flex gap-1 flex-shrink-0'>
                        <button
                            className='btn btn-sm btn-primary'
                            title={expandedKeys.size > 0 ? 'Collapse all' : 'Expand all'}
                            onClick={() => expandedKeys.size > 0 ? collapseAll() : expandAll()}
                        >
                            {expandedKeys.size > 0 ? <ChevronContract /> : <ChevronExpand />}
                        </button>
                    </div>
                </div>
                <Collapse in={showFilters}>
                    <div>
                        <div className='wt-filter-secondary'>
                            <div>
                                <SearchableStringListDropdown
                                    label='Course ID'
                                    ref={courseIdRef}
                                    options={allCourseIds}
                                    placeholder='Filter by course ID...'
                                    clearable
                                    wrapperClassName='mb-2'
                                    onChange={v => setCourseIdFilter(v)}
                                />
                            </div>
                            <div>
                                <WordSearchInput
                                    label='Course Name'
                                    ref={courseNameRef}
                                    filterFillerWords={false}
                                    wrapperClassName='mb-2'
                                    onChange={({ regex }) => setCourseNameFilter(regex)}
                                    placeholder='Filter by course name...'
                                />
                            </div>
                            <div>
                                <WordSearchInput
                                    label='Course Description'
                                    ref={courseDescRef}
                                    filterFillerWords={false}
                                    wrapperClassName='mb-2'
                                    onChange={({ regex }) => setCourseDescFilter(regex)}
                                    placeholder='Filter by course description...'
                                />
                            </div>
                        </div>
                    </div>
                </Collapse>
            </div>
            <div className='wt-table-container'>
                <table className='wt-table my-2'>
                    <colgroup>
                        <col />
                        <col />
                        <col />
                        <col />
                    </colgroup>
                    <thead>
                        <tr>
                            <th className='wt-header'>Name</th>
                            <th className='wt-header'>Description</th>
                            <th className='wt-header'>Status</th>
                            <th className='wt-header'></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredList.length > 0 ? renderRows() : renderNoList()}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default WorkdayTopicsTable;
